/** @format */

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PaidIcon from "@mui/icons-material/Paid";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { alpha } from "@mui/material/styles";
import { colors, fonts, typographyHeaderColor } from "../../../utility/types/constant";
import { fetchCirclePayoutOrder } from "../../../redux/actions/circleAction";
import {
    selectCirclePayoutOrder,
    selectIsLoadingPayoutOrder,
} from "../../../redux/selectors/circleSelector";
import { AppDispatch } from "../../../redux/store/store";

// ── Status chip config ───────────────────────────────────────────────────────
const statusChipConfig = (status: string, isNextUp: boolean) => {
    if (isNextUp || status === "DUE_TODAY")
        return {
            icon: <HourglassTopIcon sx={{ fontSize: "13px !important" }} />,
            label: status === "DUE_TODAY" ? "Due Today" : "Up Next",
            sx: {
                bgcolor: alpha(colors.mainSecondary, 0.12),
                color: colors.mainSecondary,
                border: `1px solid ${alpha(colors.mainSecondary, 0.3)}`,
            },
        };
    if (status === "COMPLETED")
        return {
            icon: <CheckCircleIcon sx={{ fontSize: "13px !important" }} />,
            label: "Completed",
            sx: { bgcolor: alpha("#2e7d32", 0.1), color: "#2e7d32", border: `1px solid ${alpha("#2e7d32", 0.25)}` },
        };
    if (status === "FAILED")
        return {
            icon: <CancelIcon sx={{ fontSize: "13px !important" }} />,
            label: "Failed",
            sx: { bgcolor: alpha("#c62828", 0.1), color: "#c62828", border: `1px solid ${alpha("#c62828", 0.25)}` },
        };
    if (status === "MISSED")
        return {
            icon: <WarningAmberIcon sx={{ fontSize: "13px !important" }} />,
            label: "Missed",
            sx: { bgcolor: alpha("#e65100", 0.1), color: "#e65100", border: `1px solid ${alpha("#e65100", 0.25)}` },
        };
    // UPCOMING / default
    return {
        icon: <CheckCircleIcon sx={{ fontSize: "13px !important" }} />,
        label: "Upcoming",
        sx: { bgcolor: alpha(colors.primary, 0.06), color: colors.primary },
    };
};

// ── Position badge colours ───────────────────────────────────────────────────
const positionStyle = (pos: number) => {
    if (pos === 1)
        return { bg: "#FFD700", color: "#7A5800", icon: <EmojiEventsIcon sx={{ fontSize: 16 }} /> };
    if (pos === 2)
        return { bg: "#C0C0C0", color: "#4A4A4A", icon: null };
    if (pos === 3)
        return { bg: "#CD7F32", color: "#fff", icon: null };
    return { bg: alpha(colors.primary, 0.08), color: colors.primary, icon: null };
};

interface PayoutOrderModalProps {
    open: boolean;
    handleClose: () => void;
    circleId: string;
}

const PayoutOrderModal = ({ open, handleClose, circleId }: PayoutOrderModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const payoutOrderResponse = useSelector(selectCirclePayoutOrder);
    const isLoading = useSelector(selectIsLoadingPayoutOrder);

    const data = payoutOrderResponse?.data ?? payoutOrderResponse;
    const payoutList: any[] = data?.payoutOrder ?? [];

    useEffect(() => {
        if (open && circleId) {
            dispatch(fetchCirclePayoutOrder(circleId));
        }
    }, [open, circleId, dispatch]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
                },
            }}>

            {/* ── Header band ─────────────────────────────────────────────── */}
            <Box
                sx={{
                    bgcolor: colors.primary,
                    px: { xs: 2.5, sm: 4 },
                    py: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: alpha(colors.mainSecondary, 0.15),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}>
                        <PaidIcon sx={{ color: colors.mainSecondary, fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: fonts.montserrat,
                                fontWeight: 800,
                                fontSize: { xs: "1rem", sm: "1.15rem" },
                                color: "#fff",
                                lineHeight: 1.2,
                            }}>
                            Payout Order
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: fonts.montserrat,
                                fontSize: "0.72rem",
                                color: alpha("#fff", 0.6),
                                mt: 0.3,
                            }}>
                            Members listed in order of scheduled payout
                        </Typography>
                    </Box>
                </Stack>

                <IconButton onClick={handleClose} sx={{ color: alpha("#fff", 0.7) }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* ── Next-up banner ───────────────────────────────────────────── */}
            <Box
                sx={{
                    bgcolor: alpha(colors.mainSecondary, 0.08),
                    borderBottom: `1px solid ${alpha(colors.mainSecondary, 0.18)}`,
                    px: { xs: 2.5, sm: 4 },
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}>
                <HourglassTopIcon sx={{ color: colors.mainSecondary, fontSize: 18 }} />
                <Typography
                    sx={{
                        fontFamily: fonts.montserrat,
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        color: colors.mainSecondary,
                    }}>
                    {data?.nextPayout
                        ? `Next payout: ${data.nextPayout.memberName} — ${data.nextPayout.payoutDateFormatted}`
                        : "No upcoming payout scheduled"}
                </Typography>
            </Box>

            {/* ── Table ────────────────────────────────────────────────────── */}
            <DialogContent sx={{ p: 0 }}>
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                        <CircularProgress size={36} sx={{ color: colors.mainSecondary }} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: alpha(colors.primary, 0.04) }}>
                                    {["#", "Member", "Cycle", "Est. Payout Date", "Amount", "Status"].map(
                                        (h) => (
                                            <TableCell
                                                key={h}
                                                sx={{
                                                    fontFamily: fonts.montserrat,
                                                    fontWeight: 700,
                                                    fontSize: "0.72rem",
                                                    color: colors.darkGrey,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.06em",
                                                    py: 1.5,
                                                }}>
                                                {h}
                                            </TableCell>
                                        ),
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {payoutList.map((row) => {
                                    const ps = positionStyle(row.position);
                                    const isNext = row.isNextUp === true;

                                    return (
                                        <TableRow
                                            key={row.memberId}
                                            sx={{
                                                bgcolor:
                                                    isNext ? alpha(colors.mainSecondary, 0.05) : "inherit",
                                                "&:hover": {
                                                    bgcolor: alpha(colors.primary, 0.03),
                                                },
                                            }}>

                                            {/* Position */}
                                            <TableCell sx={{ width: 52, pl: 2.5 }}>
                                                <Box
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: "50%",
                                                        bgcolor: ps.bg,
                                                        color: ps.color,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontFamily: fonts.montserrat,
                                                        fontWeight: 800,
                                                        fontSize: "0.75rem",
                                                        gap: 0.3,
                                                    }}>
                                                    {ps.icon ?? row.position}
                                                </Box>
                                            </TableCell>

                                            {/* Member */}
                                            <TableCell>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar
                                                        src={row.profileImage}
                                                        alt={row.memberName}
                                                        sx={{ width: 34, height: 34, fontSize: "0.85rem" }}>
                                                        {row.memberName?.charAt(0)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: fonts.montserrat,
                                                                fontWeight: 700,
                                                                fontSize: "0.82rem",
                                                                color: colors.primary,
                                                            }}>
                                                            {row.memberName}
                                                        </Typography>
                                                        {isNext && (
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: fonts.montserrat,
                                                                    fontSize: "0.68rem",
                                                                    color: colors.mainSecondary,
                                                                    fontWeight: 600,
                                                                }}>
                                                                Next in line
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </TableCell>

                                            {/* Cycle */}
                                            <TableCell>
                                                <Typography
                                                    sx={{
                                                        fontFamily: fonts.montserrat,
                                                        fontSize: "0.8rem",
                                                        color: colors.darkGrey,
                                                    }}>
                                                    Cycle {row.cycleNumber}
                                                </Typography>
                                            </TableCell>

                                            {/* Est. date */}
                                            <TableCell>
                                                <Typography
                                                    sx={{
                                                        fontFamily: fonts.montserrat,
                                                        fontSize: "0.8rem",
                                                        color: colors.darkGrey,
                                                    }}>
                                                    {row.payoutDateFormatted}
                                                </Typography>
                                            </TableCell>

                                            {/* Amount */}
                                            <TableCell>
                                                <Typography
                                                    sx={{
                                                        fontFamily: fonts.montserrat,
                                                        fontWeight: 700,
                                                        fontSize: "0.82rem",
                                                        color: colors.primary,
                                                    }}>
                                                    {row.amount != null
                                                        ? `$${(row.amount / 100).toFixed(2)}`
                                                        : "—"}
                                                </Typography>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell>
                                                {(() => {
                                                    const cfg = statusChipConfig(row.status, isNext);
                                                    return (
                                                        <Chip
                                                            icon={cfg.icon}
                                                            label={cfg.label}
                                                            size="small"
                                                            sx={{
                                                                ...cfg.sx,
                                                                fontFamily: fonts.montserrat,
                                                                fontWeight: 700,
                                                                fontSize: "0.68rem",
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Divider />

                {/* ── Footer note ─────────────────────────────────────────── */}
                <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 1.8 }}>
                    <Typography
                        sx={{
                            fontFamily: fonts.montserrat,
                            fontSize: "0.72rem",
                            color: typographyHeaderColor.secondary,
                            fontStyle: "italic",
                        }}>
                        * Payout order and estimated dates are subject to change based on
                        circle configuration and contribution activity.
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PayoutOrderModal;
